
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Language, TranslationKey } from './types';
import { translations } from './translations';
import { getLanguageFromCountry } from '@/utils/countryLanguageMapping';
import { logger } from '@/utils/logger';
import { isFrenchProperNoun } from './frenchProperNouns';

// Define the LanguageContextType
export interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  autoSetLanguageFromCountry: (countryCode: string) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Export context for class components
export { LanguageContext };

// Provider component
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // First check if user has a saved language preference
    const savedLanguage = localStorage.getItem('user-language-preference') as Language;
    if (savedLanguage && savedLanguage in translations) {
      return savedLanguage;
    }

    // Auto-detect browser language for first-time visitors
    const browserLang = navigator.language.slice(0, 2).toLowerCase() as Language;

    // If browser language is supported, use it
    if (browserLang in translations) {
      // Save auto-detected language
      localStorage.setItem('user-language-preference', browserLang);
      localStorage.setItem('language-set-by', 'browser-auto');
      return browserLang;
    }

    // Default to French as final fallback (French company)
    if ('fr' in translations) {
      localStorage.setItem('user-language-preference', 'fr');
      localStorage.setItem('language-set-by', 'default');
      return 'fr';
    }

    // Ultimate fallback to English
    return 'en';
  });

  // Enhanced setLanguage that saves user preference
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('user-language-preference', newLanguage);
    localStorage.setItem('language-set-by', 'user'); // Track how language was set
  };

  // Auto-set language based on country (only if user hasn't manually chosen)
  const autoSetLanguageFromCountry = (countryCode: string) => {
    const languageSetBy = localStorage.getItem('language-set-by');
    
    // Only auto-set if user hasn't manually chosen a language
    if (languageSetBy !== 'user') {
      const detectedLanguage = getLanguageFromCountry(countryCode);
      setLanguageState(detectedLanguage);
      localStorage.setItem('user-language-preference', detectedLanguage);
      localStorage.setItem('language-set-by', 'country-auto');
      
      
    }
  };

  // Translation function with smart fallbacks and parameter interpolation
  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {

    // 🇫🇷 OPTIMIZATION: Skip translation for French proper nouns when language is French
    // This prevents 250+ console errors for regions like "Bretagne" and sectors like "Agriculture & Bio"
    if (language === 'fr' && isFrenchProperNoun(key)) {
      return key;
    }

    // Try current language first
    let translation = translations[language]?.[key];

    // Fallback to English if current language doesn't have the key
    // Note: Only fallback for undefined, NOT for empty strings (empty = intentionally blank)
    if (translation === undefined) {
      translation = translations['en']?.[key];
      if (translation !== undefined && process.env.NODE_ENV === 'development') {
        logger.warn(`🌐 Missing translation for key: ${key} in language: ${language}, using English fallback`);
      }
    }

    // Last resort: return the key itself (for debugging)
    if (translation === undefined) {
      if (process.env.NODE_ENV === 'development') {
        logger.error(`🌐 No translation found for key: ${key} in any language`);
      }
      return key;
    }

    // Interpolate parameters if provided
    if (params) {
      return Object.entries(params).reduce((str, [paramKey, value]) => {
        return str.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      }, translation);
    }

    return translation;
  };

  // Apply language changes to document for proper text direction and locale
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      autoSetLanguageFromCountry, 
      t 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook for using the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
